import React, { useState, useEffect } from 'react';
import { MessageCircle } from 'lucide-react';
import axios from 'axios';
import ThreadList from './ThreadList';
import ThreadView from './ThreadView';
import CustomerProfile from './CustomerProfile';

function Inbox({ chatbots = [], selectedChatbotId = null, targetThreadId = null }) {
    const [selectedThread, setSelectedThread] = useState(null);
    const [filter, setFilter] = useState('open');

    // Filter by selected chatbot if one is active, otherwise show all (unified)
    const activeChatbotId = selectedChatbotId;

    // Handle incoming targetThreadId from Global Notifications
    useEffect(() => {
        if (targetThreadId) {
            // Force filter to 'open' or 'needs_attention' so the thread is visible in the list
            if (filter !== 'needs_attention' && filter !== 'all') {
                setFilter('open');
            }

            // Fetch the specific thread details
            const fetchTargetThread = async () => {
                try {
                    const response = await axios.get(`/api/threads/detail/${targetThreadId}`);
                    if (response.data) {
                        setSelectedThread(response.data);
                    }
                } catch (error) {
                    console.error('Failed to single fetch target thread:', error);
                }
            };
            fetchTargetThread();
        }
    }, [targetThreadId, filter]);

    return (
        <div className="flex h-full w-full bg-slate-50/50 p-3 gap-3 overflow-hidden">
            {/* Left Sidebar - Thread List */}
            <div className="w-[320px] h-full flex flex-col bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
                <div className="p-3 border-b border-slate-100 bg-white sticky top-0 z-10 space-y-3">
                    <div className="flex items-center justify-between">
                        <h2 className="font-bold text-base text-slate-800 tracking-tight">Inbox</h2>
                    </div>

                    <div className="flex p-1 bg-slate-100/80 rounded-lg">
                        {['open', 'needs_attention', 'closed', 'all'].map((f) => (
                            <button
                                key={f}
                                className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 capitalize ${filter === f
                                    ? 'bg-white text-slate-900 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                                    }`}
                                onClick={() => setFilter(f)}
                            >
                                {f === 'needs_attention' ? 'Human Req' : f}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <ThreadList
                        chatbotId={activeChatbotId}
                        onSelectThread={setSelectedThread}
                        selectedThreadId={selectedThread?.thread_id}
                        filter={filter}
                    />
                </div>
            </div>

            {/* Middle - Chat Area */}
            <div className="flex-1 h-full flex flex-col min-w-[400px] relative bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
                {selectedThread ? (
                    <ThreadView thread={selectedThread} />
                ) : (
                    <div className="h-full w-full flex flex-col items-center justify-center text-slate-400 bg-slate-50/30">
                        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4 text-slate-300">
                            <MessageCircle size={32} />
                        </div>
                        <p className="text-sm font-medium text-slate-500">Select a conversation to start chatting</p>
                    </div>
                )}
            </div>

            {/* Right Sidebar - Details Panel */}
            {selectedThread && (
                <div className="w-[380px] h-full bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden hidden xl:block">
                    <div className="h-full overflow-y-auto custom-scrollbar">
                        <CustomerProfile thread={selectedThread} customer={selectedThread?.customer} />
                    </div>
                </div>
            )}
        </div>
    );
}

export default Inbox;
